/**
 * Facility-side reads and writes for VisitStart: the clinic's check-in inbox, and
 * the clinic's own intake configuration.
 *
 * Both live under `locations/{locationId}` because that is where this repo puts
 * facility-scoped data — the same place as `visits` and `queue` — and because for
 * the check-ins the path IS the permission: the rules gate them on membership of
 * the location in the URL, so one document read covers a whole query.
 *
 * Reading needs no Cloud Function. A clinic reading intake a patient just gave it
 * on the clinic's own poster is not a disclosure to a third party, so it needs no
 * access-accounting callable; the audited path is the 7-digit code, where a
 * DIFFERENT provider redeems a patient's chart. See redeemHealthRecordShareToken.
 *
 * Nor does anything else here: this module only reads check-ins and writes the
 * clinic's own config. firestore.rules blocks `delete` outright on a check-in
 * (it is a clinical record) and staff have no client write path to one at all,
 * and there is no longer a callable that gives them one either.
 */

import firebase from "firebase/compat/app";
import "firebase/compat/functions";
// No default import: the callable that needed it went with the dismiss action.
import { db } from "services/firebase";

const LOCATIONS_COLLECTION = "locations";
const VISIT_START_SESSIONS_SUBCOLLECTION = "visitStartSessions";
const VISIT_START_CONFIG_SUBCOLLECTION = "visitStartConfig";
const CONFIG_DOC_ID = "profile";

/** How many check-ins the inbox holds. Matches subscribeRecentVisits. */
export const CHECK_IN_PAGE_SIZE = 100;

const checkInsCollection = (clinicId) =>
  db
    .collection(LOCATIONS_COLLECTION)
    .doc(String(clinicId))
    .collection(VISIT_START_SESSIONS_SUBCOLLECTION);

export const visitStartConfigRef = (clinicId) =>
  db
    .collection(LOCATIONS_COLLECTION)
    .doc(String(clinicId))
    .collection(VISIT_START_CONFIG_SUBCOLLECTION)
    .doc(CONFIG_DOC_ID);

/**
 * Live list of this clinic's check-ins, newest activity first.
 *
 * Ordered by `updatedAt` rather than `createdAt` so a check-in in progress rises
 * as the patient answers — the front desk wants to know who is mid-intake right
 * now, not who arrived first.
 *
 * One orderBy and no where clause, so there is no composite index to deploy.
 * Filtering by status happens in the component over the returned rows, the same
 * trade subscribeRecentVisits makes: a clinic's daily volume is far below the
 * page size, and an index is a deploy step that can be forgotten.
 *
 * @returns {function} unsubscribe
 */
export const subscribeVisitStartCheckIns = (clinicId, onChange, onError) => {
  if (!clinicId) return () => {};
  return checkInsCollection(clinicId)
    .orderBy("updatedAt", "desc")
    .limit(CHECK_IN_PAGE_SIZE)
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
};

/**
 * One check-in, live. A subscription rather than a get so a desk that opens a
 * patient's answers while they are still answering sees the rest arrive.
 *
 * @returns {function} unsubscribe
 */
export const subscribeVisitStartCheckIn = (
  clinicId,
  sessionId,
  onChange,
  onError,
) => {
  if (!clinicId || !sessionId) return () => {};
  return checkInsCollection(clinicId)
    .doc(sessionId)
    .onSnapshot(
      (snap) => onChange(snap.exists ? { id: snap.id, ...snap.data() } : null),
      onError,
    );
};

/**
 * This clinic's VisitStart configuration, live. A subscription rather than a
 * get so a change made from Settings (or from this page's own "turn on" button)
 * is reflected everywhere that reads it without a manual refresh or a remount.
 *
 * @returns {function} unsubscribe
 */
export const subscribeVisitStartConfig = (clinicId, onChange, onError) => {
  if (!clinicId) return () => {};
  return visitStartConfigRef(clinicId).onSnapshot(
    (snap) => onChange(snap.exists ? snap.data() : null),
    onError,
  );
};

/**
 * This clinic's VisitStart configuration, or null if it has never been set.
 *
 * Null is not "disabled" — it is the pre-configuration state, and
 * resolveEnabledElements already reads it as "catalog defaults". The two are
 * distinguished because a clinic that has never opened this page should still get
 * a sensible questionnaire if someone scans a link, whereas a clinic that
 * deliberately switched VisitStart off should not.
 */
export const getVisitStartConfig = async (clinicId) => {
  if (!clinicId) return null;
  const snap = await visitStartConfigRef(clinicId).get();
  return snap.exists ? snap.data() : null;
};

/**
 * Save this clinic's configuration.
 *
 * `enabledElements` is the CONFIGURABLE selection only. Core elements are re-added
 * by buildEnabledElements when a session resolves its question set, so they are
 * not stored here — writing them would mean a catalog change to which elements are
 * mandatory silently disagreed with every clinic's saved profile.
 *
 * An empty array is a real choice (core questions only) and is stored as one. The
 * rules cap the list at 20 and reject unknown keys.
 */
export const saveVisitStartConfig = async (
  clinicId,
  { enabled, enabledElements, updatedBy },
) => {
  if (!clinicId) throw new Error("Missing clinic.");
  await visitStartConfigRef(clinicId).set({
    enabled: Boolean(enabled),
    enabledElements: Array.isArray(enabledElements) ? enabledElements : [],
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedBy: updatedBy || null,
  });
};

/*
 * There is no write path here for the desk. The inbox used to offer a "Remove
 * from list" action, which called a dismissVisitStartCheckIn callable to set
 * `dismissedByClinic`; both are gone. The inbox still SKIPS rows carrying that
 * flag, because rows dismissed while the action existed were hidden
 * deliberately -- see the filter in VisitStartCheckInsPage.
 */
